import { useState, useEffect, useRef } from 'react';
import { Task, TaskStatus } from '../types';

const PRIORITY_OPTIONS: Array<{ value: Task['priority']; label: string; style: string }> = [
  { value: 'urgent', label: '⚡ Urgent', style: 'bg-red-900/50 text-red-300 border border-red-700/50' },
  { value: 'normal', label: '● Normal', style: 'bg-amber-900/40 text-amber-300 border border-amber-700/40' },
  { value: 'low',    label: '○ Low',    style: 'bg-gray-800 text-gray-400 border border-gray-700' },
];

const SOURCE_OPTIONS: Array<{ value: Task['source']; label: string; style: string }> = [
  { value: 'gmail',  label: '✉ Gmail',  style: 'bg-blue-900/40 text-blue-300 border border-blue-700/40' },
  { value: 'slack',  label: '# Slack',  style: 'bg-purple-900/40 text-purple-300 border border-purple-700/40' },
  { value: 'calls',  label: '◎ Calls',  style: 'bg-green-900/40 text-green-300 border border-green-700/40' },
  { value: 'manual', label: '✦ Manual', style: 'bg-gray-800 text-gray-400 border border-gray-700' },
];

const STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: 'p1_today',    label: 'P1 — Must Be Done Today' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked',     label: 'Blocked' },
  { value: 'completed',   label: 'Completed' },
];

interface Props {
  task: Task | null;
  onClose: () => void;
  onUpdate: (id: string, patch: Partial<Task>) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function isVip(task: Task): boolean {
  return `${task.title} ${task.notes ?? ''}`.toLowerCase().includes('vip');
}

export function TaskDetailPanel({ task, onClose, onUpdate }: Props) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [editingDate, setEditingDate] = useState(false);
  const [editingProject, setEditingProject] = useState(false);
  const [projectValue, setProjectValue] = useState('');

  const titleInputRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Reset local edit state whenever a different task is opened
  useEffect(() => {
    setEditingTitle(false);
    setEditingNotes(false);
    setEditingDate(false);
    setEditingProject(false);
  }, [task?.id]);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.select();
  }, [editingTitle]);

  useEffect(() => {
    if (editingNotes) {
      const el = notesRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }
  }, [editingNotes]);

  if (!task) return null;

  const wasEdited =
    new Date(task.updatedAt).getTime() - new Date(task.createdAt).getTime() > 60_000;

  // ─── Commit helpers ──────────────────────────────────────────────────────────

  function commitTitle() {
    const trimmed = titleValue.trim();
    if (trimmed && trimmed !== task!.title) onUpdate(task!.id, { title: trimmed });
    setEditingTitle(false);
  }

  function commitNotes() {
    const trimmed = notesValue.trim();
    if (trimmed !== (task!.notes ?? '')) onUpdate(task!.id, { notes: trimmed || undefined });
    setEditingNotes(false);
  }

  function handleProjectCommit() {
    const trimmed = projectValue.trim();
    onUpdate(task!.id, { project: trimmed || undefined });
    setEditingProject(false);
  }

  const vip = isVip(task);
  const currentSource = SOURCE_OPTIONS.find((o) => o.value === task.source) ?? SOURCE_OPTIONS[3];
  const currentPriority = PRIORITY_OPTIONS.find((o) => o.value === task.priority) ?? PRIORITY_OPTIONS[1];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Slide-in panel */}
      <div className="fixed right-0 top-0 h-full w-[420px] bg-gray-900 border-l border-gray-800 z-50 flex flex-col shadow-2xl">

        {/* ── Header / title ─────────────────────────────────────────────────── */}
        <div className="flex items-start gap-3 p-5 border-b border-gray-800 shrink-0">
          <div className="flex-1 min-w-0">
            {vip && (
              <span className="inline-block text-xs bg-yellow-900/50 text-yellow-300 border border-yellow-700/50 px-2 py-0.5 rounded mb-2">
                ★ VIP
              </span>
            )}
            {editingTitle ? (
              <input
                ref={titleInputRef}
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                className="w-full text-sm font-semibold text-white bg-gray-800 border border-indigo-500 rounded px-2 py-1 focus:outline-none leading-snug"
              />
            ) : (
              <button
                className="text-left w-full group"
                onClick={() => { setTitleValue(task.title); setEditingTitle(true); }}
                title="Click to edit title"
              >
                <h2 className="text-sm font-semibold text-white leading-snug group-hover:text-indigo-300 transition">
                  {task.title}
                  <span className="ml-1.5 text-gray-700 group-hover:text-gray-500 text-xs">✎</span>
                </h2>
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-xl leading-none shrink-0 mt-0.5 transition"
          >
            ×
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Notes / context */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Context</p>
            {editingNotes ? (
              <textarea
                ref={notesRef}
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                onBlur={commitNotes}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setEditingNotes(false); }
                  if (e.key === 'Enter' && e.metaKey) commitNotes();
                }}
                rows={5}
                className="w-full text-sm text-gray-200 bg-gray-800 border border-indigo-500 rounded px-3 py-2 focus:outline-none resize-none leading-relaxed"
                placeholder="Add context, links, or notes…"
              />
            ) : task.notes ? (
              <button
                className="text-left w-full group"
                onClick={() => { setNotesValue(task.notes ?? ''); setEditingNotes(true); }}
                title="Click to edit context"
              >
                <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap group-hover:text-white transition">
                  {task.notes}
                  <span className="ml-1.5 text-gray-700 group-hover:text-gray-500 text-xs">✎</span>
                </p>
              </button>
            ) : (
              <button
                onClick={() => { setNotesValue(''); setEditingNotes(true); }}
                className="text-sm text-gray-600 hover:text-gray-400 transition"
              >
                + Add context
              </button>
            )}
          </section>

          {/* Source */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Source</p>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={task.source}
                onChange={(e) => onUpdate(task.id, { source: e.target.value as Task['source'] })}
                className={`text-xs px-2 py-1 rounded border focus:outline-none focus:border-indigo-500 cursor-pointer ${currentSource.style} bg-transparent`}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="bg-gray-900 text-gray-200">
                    {o.label}
                  </option>
                ))}
              </select>
              {task.sourceLink && (
                <a
                  href={task.sourceLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-300 underline truncate max-w-[220px] transition"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open source ↗
                </a>
              )}
            </div>
          </section>

          {/* Priority */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Priority</p>
            <div className="flex gap-1.5 flex-wrap">
              {PRIORITY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => { if (o.value !== task.priority) onUpdate(task.id, { priority: o.value }); }}
                  className={`text-xs px-2 py-1 rounded transition ${
                    task.priority === o.value
                      ? o.style
                      : 'bg-gray-900 text-gray-600 border border-gray-800 hover:border-gray-600 hover:text-gray-400'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </section>

          {/* Status */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Status</p>
            <select
              value={task.status}
              onChange={(e) => {
                const status = e.target.value as TaskStatus;
                onUpdate(task.id, { status, completed: status === 'completed' });
              }}
              className="text-sm bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-gray-200 focus:outline-none focus:border-indigo-500 w-full"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </section>

          {/* Due date */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Due Date</p>
            {editingDate ? (
              <input
                type="date"
                defaultValue={task.dueDate?.slice(0, 10) ?? ''}
                autoFocus
                className="text-sm bg-gray-800 border border-indigo-500 rounded px-2 py-1.5 text-gray-200 focus:outline-none w-full"
                onChange={(e) => {
                  onUpdate(task.id, { dueDate: e.target.value || undefined });
                  setEditingDate(false);
                }}
                onBlur={() => setEditingDate(false)}
              />
            ) : (
              <button
                onClick={() => setEditingDate(true)}
                className="text-sm text-gray-400 hover:text-gray-200 transition text-left"
              >
                {task.dueDate ? formatDate(task.dueDate) : (
                  <span className="text-gray-600 hover:text-gray-400">+ Add due date</span>
                )}
              </button>
            )}
          </section>

          {/* Project */}
          <section>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Project</p>
            {editingProject ? (
              <input
                type="text"
                value={projectValue}
                autoFocus
                className="text-sm bg-gray-800 border border-indigo-500 rounded px-2 py-1.5 text-gray-200 focus:outline-none w-full"
                onChange={(e) => setProjectValue(e.target.value)}
                onBlur={handleProjectCommit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleProjectCommit();
                  if (e.key === 'Escape') setEditingProject(false);
                }}
              />
            ) : (
              <button
                onClick={() => { setProjectValue(task.project ?? ''); setEditingProject(true); }}
                className="text-sm text-left transition"
              >
                {task.project ? (
                  <span className="text-indigo-300 hover:text-indigo-200">⌁ {task.project}</span>
                ) : (
                  <span className="text-gray-600 hover:text-gray-400">+ Add project</span>
                )}
              </button>
            )}
          </section>

          {/* Timestamps */}
          <section className="pt-1 border-t border-gray-800 space-y-1.5">
            <p className="text-xs text-gray-600">Created {formatDateTime(task.createdAt)}</p>
            {wasEdited && (
              <p className="text-xs text-gray-600">
                <span className="text-gray-500 mr-1">✎</span>
                Edited {formatDateTime(task.updatedAt)}
              </p>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
